import { FC, useEffect, useRef, useState } from 'react';
import { Box, Container, Divider, FormControl, Stack } from '@mui/material';
import { useDomainsQuery } from '../../graphql/generated/operations/domains.ts';
import { LoadingBackdrop } from '../../components/loading.backdrop.tsx';
import { Form, Formik, FormikContextType } from 'formik';
import * as yup from 'yup';
import { LargeDomain } from '../../components/domain/large-domain.tsx';
import { DotStepper } from '../../components/dot-stepper.tsx';
import { AutoCompleteInput } from '../../components/forms/auto-complete-input.tsx';
import { SliderInput } from '../../components/forms/slider-input.tsx';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../../constants/paths.ts';
import { useUpdateUserPreferenceMutation } from '../../graphql/generated/operations/user.ts';
import { useSnackbar } from '../../contexts/snackbar.context.tsx';
import { trackEvent } from '../../utils/analytics.ts';

export const UserProfilePage: FC = () => {
  const { data, loading } = useDomainsQuery();
  const [updateUser, { data: saved, loading: saving }] = useUpdateUserPreferenceMutation();
  const { push } = useSnackbar();
  const [domainIndex, setDomainIndex] = useState(0);
  const formikRef = useRef<FormikContextType<any>>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (saved) {
      navigate(Paths.LET_GET_STARTED);
    }
  }, [saved]);

  if (!data || loading || saving) {
    return <LoadingBackdrop />;
  }
  return (
    <Container sx={{ mt: 3, pb: 10 }} maxWidth="md">
      <Formik
        enableReinitialize={true}
        initialValues={{
          domains: data.domains.reduce((acc: any, domain) => {
            acc[domain._id] = {};
            return acc;
          }, {})
        }}
        validationSchema={yup.object({
          domains: yup.object(
            data.domains.reduce((acc: any, domain) => {
              acc[domain._id] = yup.object({
                importance: yup.number().required('Please select an importance rating'),
                time: yup.string().required('Please select a time satisfaction option')
              });
              return acc;
            }, {})
          )
        })}
        innerRef={formikRef}
        onSubmit={async (values) => {
          await updateUser({
            variables: {
              preference: Object.keys(values.domains).map((domainId: string) => ({
                domainId,
                importance: values.domains[domainId].importance.toString(),
                time: values.domains[domainId].time
              }))
            }
          });
          navigate(Paths.ENABLE_TRACKING);
        }}
      >
        <Form>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {data.domains.map((domain, index) => (
              <Stack key={domain._id} gap={2} sx={{ display: domainIndex === index ? 'flex' : 'none', flexDirection: 'column' }}>
                <LargeDomain domain={domain} />
                <Box sx={{ mt: 4 }} />
                <FormControl>How important is {domain?.timeText} to you?</FormControl>
                <Box sx={{ mx: 6 }}>
                  <SliderInput
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 1, label: 'Not Important' },
                      { value: 5, label: 'Very important' }
                    ]}
                    name={`domains.${domain?._id}.importance`}
                    min={1}
                    max={5}
                    step={1}
                    sx={{ mt: 4 }}
                  />
                </Box>
                <Divider />
                <FormControl>Do you spend as much time as you would like {domain?.timeText}?</FormControl>
                <AutoCompleteInput
                  name={`domains.${domain?._id}.time`}
                  options={[
                    {
                      label: 'I am happy with the time I spend',
                      id: 'happy'
                    },
                    {
                      label: 'I spend less time than I would like',
                      id: 'more'
                    }
                  ]}
                />
              </Stack>
            ))}
            <Box sx={{ mt: 4 }} />
            <DotStepper
              steps={data?.domains.length || 0}
              activeStep={domainIndex}
              onBack={() => {
                setDomainIndex(Math.max(domainIndex - 1, 0));
                trackEvent('user_profile_back', { step: domainIndex });
              }}
              onNext={() => {
                const currentValues = formikRef?.current?.values ?? { domains: {} };
                const currentDomainId: string = (data?.domains as any)[domainIndex]._id;
                const currentDomain = currentValues?.domains[currentDomainId] ?? {};
                if (currentDomain.importance === undefined || currentDomain.time === undefined) {
                  if (currentDomain.importance === undefined) {
                    formikRef.current?.setFieldTouched(`domains.${currentDomainId}.importance`, true);
                  }
                  if (currentDomain.time === undefined) {
                    formikRef.current?.setFieldTouched(`domains.${currentDomainId}.time`, true);
                  }
                  push({ message: 'Please fill in all required fields', type: 'error' });
                  trackEvent('user_profile_validation_error', { step: domainIndex });
                } else {
                  const nextStep = Math.min(domainIndex + 1, data?.domains.length);
                  setDomainIndex(nextStep);
                  trackEvent('user_profile_next', { step: nextStep });
                }
              }}
              onCompleted={() => {
                trackEvent('user_profile_complete');
                formikRef.current?.submitForm();
              }}
            />
          </Box>
        </Form>
      </Formik>
    </Container>
  );
};
